     // Global initialization flags
window.imageSearchInitialized = window.imageSearchInitialized || false;

class ImageSearch {
  constructor() {
    // Static class properties for tracking state
    ImageSearch.modelLoadingStarted = ImageSearch.modelLoadingStarted || false;
    
    this.model = null;
    this.SIMILARITY_THRESHOLD = 0.80;
    this.setupUI();
    // Don't load model immediately - wait until needed
  }

  async initModel() {
    // Only log once
    if (!ImageSearch.modelLoadingStarted) {
      ImageSearch.modelLoadingStarted = true;
      console.log('Loading image analysis capabilities...');
    }

    // Don't reload if already loading or loaded
    if (this.modelLoading || this.model) {
      return;
    }
    
    this.modelLoading = true;
    
    try {
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 100; // 10 seconds max
        
        const check = () => {
          attempts++;
          if (window.tf && window.mobilenet) {
            resolve();
          } else if (attempts >= maxAttempts) {
            reject(new Error('TensorFlow or MobileNet not available after timeout'));
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });

      // Load high-precision model
      this.model = await window.mobilenet.load({
        version: 2,
        alpha: 1.0,
        inputRange: [0, 1]
      });
      console.log('Model loaded successfully');
    } catch (error) {
      console.error('Error loading model:', error);
    } finally {
      this.modelLoading = false;
    }
  }

  setupUI() {
    // Check if already set up
    if (document.querySelector('.image-search-container')) {
      return;
    }

    const searchInputWrapper = document.querySelector('.input-group__field--connected');
    if (!searchInputWrapper) {
      console.error('Search input wrapper not found');
      return;
    }

    const container = document.createElement('div');
    container.className = 'image-search-container';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.className = 'image-search-input';
    fileInput.style.display = 'none';

    const button = document.createElement('button');
button.type = 'button';
button.className = 'search-button__image';
button.setAttribute('aria-label', 'Search by image');

const img = document.createElement('img');
img.src = "{{ 'search-icon.svg' | asset_url }}";
img.alt = "Search by image";
img.style.width = "20px";
img.style.height = "20px";

button.appendChild(img);


    const dropdown = document.createElement('div');
    dropdown.className = 'image-search-results';
    dropdown.style.display = 'none';
    dropdown.style.position = 'absolute';
    dropdown.style.zIndex = '1000';
    dropdown.style.background = 'white';
    dropdown.style.border = '1px solid #ddd';
    dropdown.style.borderRadius = '4px';
    dropdown.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
    dropdown.style.width = '300px';
    dropdown.style.maxHeight = '500px';
    dropdown.style.overflowY = 'auto';

    // Add processing flag to prevent multiple simultaneous operations
    let isProcessing = false;
    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (file && !isProcessing) {
        isProcessing = true;
        console.log('File selected:', file.name, 'Type:', file.type);
        try {
          await this.processImage(file, dropdown);
        } finally {
          isProcessing = false;
        }
      }
    });

    button.addEventListener('click', (e) => {
      e.preventDefault();
      console.log('Camera button clicked');
      fileInput.click();
    });

    // Use a more precise document click handler
    const clickHandler = (e) => {
      if (!dropdown.contains(e.target) && !button.contains(e.target) && dropdown.style.display !== 'none') {
        this.closeDropdown(dropdown, fileInput);
      }
    };
    document.addEventListener('click', clickHandler);

    container.appendChild(fileInput);
    container.appendChild(button);
    searchInputWrapper.appendChild(container);
    searchInputWrapper.parentElement.appendChild(dropdown);
  }

  closeDropdown(dropdown, fileInput) {
    dropdown.style.display = 'none';
    fileInput.value = '';
  }

  async processImage(file, dropdown) {
    try {
      dropdown.innerHTML = '<div class="loading" style="padding: 15px; text-align: center;">Analyzing image in detail...</div>';
      dropdown.style.display = 'block';

      if (!this.model) {
        dropdown.innerHTML = '<div class="loading" style="padding: 15px; text-align: center;">Loading image analysis capabilities...</div>';
        await this.initModel();
      }

      const img = await this.createImageFromFile(file);
      const imageSignature = await this.generateImageSignature(img);
      const results = await this.findSimilarProducts(imageSignature);
      this.displayResults(results, dropdown);
    } catch (error) {
      console.error('Error:', error);
      dropdown.innerHTML = '<div class="error" style="padding: 15px; color: red; text-align: center;">Error processing image. Please try again.</div>';
    }
  }

  async generateImageSignature(img) {
    // Extract multiple features for better matching
    let tensor = null, resized = null, normalized = null, features = null, detailedFeatures = null;
    
    try {
      tensor = tf.browser.fromPixels(img);
      resized = tf.image.resizeBilinear(tensor, [224, 224]);
      normalized = tf.div(resized, 255);
      
      // Get high-level features
      features = await this.model.infer(normalized, true);
      
      // Get detailed features from intermediate layer
      detailedFeatures = await this.model.infer(normalized, false);
      
      // Combine features for better matching
      const signature = {
        highLevel: await features.array(),
        detailed: await detailedFeatures.array(),
        imageStats: await this.computeImageStats(normalized)
      };
      
      return signature;
    } catch (error) {
      console.error('Error generating image signature:', error);
      throw error;
    } finally {
      // Ensure tensors are disposed even if there's an error
      if (tensor) tensor.dispose();
      if (resized) resized.dispose();
      if (normalized) normalized.dispose();
      if (features) features.dispose();
      if (detailedFeatures) detailedFeatures.dispose();
    }
  }
  
  async computeImageStats(tensor) {
    let mean = null, std = null;
    
    try {
      // Calculate image statistics for better matching
      mean = tf.mean(tensor, [0, 1]);
      const moments = tf.moments(tensor, [0, 1]);
      std = moments.variance.sqrt();
      
      const stats = {
        mean: await mean.array(),
        std: await std.array()
      };
      
      // Clean up any other tensors created by moments
      if (moments.mean) moments.mean.dispose();
      if (moments.variance) moments.variance.dispose();
      
      return stats;
    } catch (error) {
      console.error('Error computing image stats:', error);
      throw error;
    } finally {
      // Ensure tensors are disposed even if there's an error
      if (mean) mean.dispose();
      if (std) std.dispose();
    }
  }

  async findSimilarProducts(querySignature) {
    try {
      const response = await fetch('/products.json?limit=50');
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }
      
      const data = await response.json();
      const results = [];
      
      // Process only a limited number of products to prevent browser freezing
      const productsToProcess = data.products.slice(0, 20);
      
      for (const product of productsToProcess) {
        if (product.images?.length) {
          try {
            const img = await this.loadImage(product.images[0].src);
            const productSignature = await this.generateImageSignature(img);
            
            const similarity = this.computeDetailedSimilarity(querySignature, productSignature);
            
            if (similarity >= this.SIMILARITY_THRESHOLD) {
              results.push({
                product,
                similarity
              });
              console.log(`Match found: ${product.title} (${Math.round(similarity * 100)}% similar)`);
            }
          } catch (error) {
            console.error(`Error processing product ${product.id}:`, error);
            // Continue with other products
          }
        }
      }
      
      return results
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, 4);
    } catch (error) {
      console.error('Error finding similar products:', error);
      return [];
    }
  }

  computeDetailedSimilarity(sig1, sig2) {
    try {
      // Compute high-level feature similarity
      const highLevelSim = this.cosineSimilarity(
        sig1.highLevel[0],
        sig2.highLevel[0]
      );
      
      // Compute detailed feature similarity
      const detailedSim = this.cosineSimilarity(
        sig1.detailed[0],
        sig2.detailed[0]
      );
      
      // Compute color statistics similarity
      const colorSim = this.computeColorSimilarity(
        sig1.imageStats,
        sig2.imageStats
      );
      
      // Weighted combination of similarities
      return (highLevelSim * 0.4) + (detailedSim * 0.4) + (colorSim * 0.2);
    } catch (error) {
      console.error('Error computing similarity:', error);
      return 0;
    }
  }
  
  computeColorSimilarity(stats1, stats2) {
    try {
      const meanDiff = stats1.mean.map((val, i) => 
        Math.abs(val - stats2.mean[i])
      ).reduce((a, b) => a + b, 0) / stats1.mean.length;
      
      const stdDiff = stats1.std.map((val, i) => 
        Math.abs(val - stats2.std[i])
      ).reduce((a, b) => a + b, 0) / stats1.std.length;
      
      return 1 - ((meanDiff + stdDiff) / 2);
    } catch (error) {
      console.error('Error computing color similarity:', error);
      return 0;
    }
  }
  
  cosineSimilarity(vec1, vec2) {
    try {
      const dotProduct = vec1.reduce((sum, val, i) => sum + val * vec2[i], 0);
      const mag1 = Math.sqrt(vec1.reduce((sum, val) => sum + val * val, 0));
      const mag2 = Math.sqrt(vec2.reduce((sum, val) => sum + val * val, 0));
      
      // Prevent division by zero
      if (mag1 === 0 || mag2 === 0) {
        return 0;
      }
      
      return dotProduct / (mag1 * mag2);
    } catch (error) {
      console.error('Error computing cosine similarity:', error);
      return 0;
    }
  }
  
  createImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => {
          console.error('Error loading image:', err);
          reject(new Error('Failed to load image'));
        };
        img.src = e.target.result;
      };
      reader.onerror = (err) => {
        console.error('Error reading file:', err);
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  }
  
  loadImage(url) {
    return new Promise((resolve, reject) => {
      // Process Shopify image URLs
      if (url.includes('cdn.shopify.com')) {
        // Remove size parameters to get full image
        url = url.split('?')[0];
      }
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => {
        console.error('Failed to load image:', url);
        reject(new Error('Failed to load product image'));
      };
      
      // Add cache-busting parameter
      img.src = url + (url.includes('?') ? '&' : '?') + 'cache=' + Date.now();
      
      // Add timeout to prevent hanging
      setTimeout(() => {
        if (!img.complete) {
          img.src = ''; // Cancel the image request
          reject(new Error('Image load timed out'));
        }
      }, 5000);
    });
  }

  displayResults(results, dropdown) {
    try {
      console.log('Displaying results:', results);
      const closeButton = document.createElement('button');
      closeButton.className = 'close-dropdown';
      closeButton.innerHTML = '×';
      closeButton.style.position = 'absolute';
      closeButton.style.right = '10px';
      closeButton.style.top = '10px';
      closeButton.style.background = 'none';
      closeButton.style.border = 'none';
      closeButton.style.fontSize = '20px';
      closeButton.style.cursor = 'pointer';
      closeButton.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const fileInput = document.querySelector('.image-search-input');
        this.closeDropdown(dropdown, fileInput);
      };

      const container = document.createElement('div');
      container.className = 'search-results-container';
      container.style.padding = '15px';

      if (!results || results.length === 0) {
        container.innerHTML = '<div class="no-results" style="text-align: center; padding: 20px;">No similar products found</div>';
      } else {
        results.forEach(({ product, similarity }) => {
          if (!product || !product.images || !product.images[0]) {
            return;
          }
          
          const resultDiv = document.createElement('div');
          resultDiv.className = 'search-result-item';
          resultDiv.style.display = 'flex';
          resultDiv.style.margin = '10px 0';
          resultDiv.style.padding = '10px';
          resultDiv.style.border = '1px solid #eee';
          resultDiv.style.borderRadius = '4px';
          
          const similarityPercent = Math.round(similarity * 100);

          resultDiv.innerHTML = `
            <div class="result-image" style="width: 80px; height: 80px; margin-right: 10px;">
              <img src="${product.images[0].src}" alt="${product.title}" style="width: 100%; height: 100%; object-fit: cover;">
            </div>
            <div class="result-details" style="flex: 1;">
              <h3 style="margin: 0 0 5px; font-size: 14px;">${product.title}</h3>
              <p class="price" style="margin: 0 0 5px; font-weight: bold;">${(product.variants[0]?.price || '0.00')} ${window.Shopify?.currency?.active || 'USD'}</p>
              <div class="match-score" style="margin: 5px 0;">
                <div class="score-bar" style="height: 4px; background: #eee; border-radius: 2px; overflow: hidden;">
                  <div style="width: ${similarityPercent}%; height: 100%; background: #4CAF50;"></div>
                </div>
                <span style="font-size: 12px; color: #666;">${similarityPercent}% Match</span>
              </div>
              <a href="/products/${product.handle}" class="view-product" style="display: inline-block; padding: 4px 8px; background: #000000; color: #333; text-decoration: none; border-radius: 4px; font-size: 12px; cursor: pointer;" onclick="window.location.href='/products/${product.handle}'; return false;">View Product</a>
            </div>
          `;
          container.appendChild(resultDiv);
        });
      }

      dropdown.innerHTML = '';
      dropdown.appendChild(closeButton);
      dropdown.appendChild(container);
    } catch (error) {
      console.error('Error in displayResults:', error);
      dropdown.innerHTML = '<div class="error" style="padding: 15px; color: red; text-align: center;">Error displaying results. Please try again.</div>';
    }
  }
}

// Only initialize once when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if already initialized
  if (window.imageSearchInitialized) {
    return;
  }
  
  // Mark as initialized
  window.imageSearchInitialized = true;
  
  // Log once
  console.log('Image optimization module standing by...');
  
  // Check if we're in the Shopify Theme Editor
  if (window.Shopify && window.Shopify.designMode) {
    console.log('In Shopify Theme Editor - not initializing image search');
    return;
  }
  
  // Initialize with a slight delay to ensure DOM is fully ready
  setTimeout(() => {
    if (!document.querySelector('.image-search-container')) {
      new ImageSearch();
    }
  }, 500);
});
